<template>
  <Collapse
    class="project-rep"
    :title="$t('project.representative.title')"
    :enableExpandByClickingTitle="true"
    :withBorder="false"
    arrowStyle="accordion"
  >
    <div
      slot="content"
      class="project-rep__box"
    >
      <div class="project-rep__header">
        <div class="project-rep__title">
          {{ $t("project.representative.subTitle") }}
        </div>
        <div
          v-if="isPicView"
          class="project-rep__btns"
        >
          <HKLButton
            v-if="!editable"
            color="darkblue"
            action="edit"
            :disabled="!canEditRepresentative"
            @click="() => (editable = true)"
          />
          <HKLButton
            v-if="editable"
            v-hkldreset
            color="darkblue"
            action="cancel"
            @click="() => cancel()"
          />
          <HKLButton
            v-if="editable"
            v-hkldreset
            color="orange"
            action="save"
            @click="() => save()"
          />
        </div>
      </div>
      <div
        v-if="editable"
        class="flex-table project-rep__form"
      >
        <div
          class="flex-table__header flex-table__header-label"
        >
          <label
            v-for="(headItem) in editHeadersMap"
            :key="headItem.labelKey"
            class="flex-table__col"
            :class="[headItem.labelKey, headItem.required ? 'required-flag' : '']"
          >
            {{ $t(`table.header.${headItem.labelKey}`) }}
          </label>
          <HKLDeleteIcon class="flex-table__del-col hidden" />
        </div>
        <div
          v-for="(item, index) in pendingAccounts"
          :key="index"
          class="flex-table__body"
        >
          <div class="flex-table__col input-name">
            <HKLInput
              v-for="fullnameObj of sortedFullName"
              :key="fullnameObj.value"
              v-model="item[fullnameObj.value]"
              v-fusioninputcheck="{
                type: 'string',
                required: true,
              }"
              :placeholder="$t(`contact.basic.${fullnameObj.label}`)"
            />
          </div>
          <div
            class="flex-table__col input-email"
          >
            <HKLInputEmail
              v-model="item.email"
              :class="invalidEmailClassesMap[index]"
              :checkerOptions="{
                type: 'String',
                required: true,
                patternOnly: () => checkInputEmail(item.email),
                showTip: false,
              }"
            />
          </div>
          <div class="flex-table__col input-phone">
            <HKLInput
              v-model="item.phone"
              v-fusioninputcheck="{
                type: 'string',
                required: true,
              }"
            />
          </div>
          <div class="flex-table__col input-otherPhone">
            <HKLInput
              v-model="item.altPhone"
            />
          </div>
          <HKLDeleteIcon @click="() => preDeleteAccount(item, index)" />
        </div>
        <div
          class="project-rep__add underline pointer"
          @click="() => addAccount()"
        >
          {{ $t("action.addProjectRep") }}+
        </div>
      </div>
      <div
        v-else
        :class="['flex-table', { creator: canEditRepresentative }]"
      >
        <div class="flex-table__header">
          <span
            v-for="(item, index) in headersMap"
            :key="index"
            class="flex-table__col"
            :class="item.labelKey"
          >{{ $t(`table.header.${item.labelKey}`) }}</span>
          <span
            v-show="canEditRepresentative"
            class="invite-col"
          />
        </div>
        <div
          v-for="(item, i) in pendingAccounts"
          :key="i"
          class="flex-table__body"
        >
          <span
            v-for="(headItem) in headersMap"
            :key="headItem.labelKey"
            class="flex-table__col"
            :class="headItem.labelKey"
          >{{ item[headItem.labelKey] | isEmptyValue }}</span>
          <span
            v-show="canEditRepresentative"
            class="invite-col"
            :class="item.showInviteClass"
          >
            <HKLButton
              color="orange"
              action="invite"
              @click="() => invite(item.id)"
            />
          </span>
        </div>
      </div>
      <HKLModal
        ref="delModalRef"
        @onOkClick="() => delAccount(deleteIndex)"
      >
        <template
          #title
        >
          {{ $t("hint.deleteProjectRepresentative") }}
        </template>
      </HKLModal>
      <HKLInviteModal
        ref="inviteAllRef"
        :listTitle="$t('project.representative.subTitle')"
        :accountsList="needInviteAccounts"
        @onInviteClick="() => inviteAll(needInviteAccounts)"
      />
    </div>
  </Collapse>
</template>

<script>
import lodash from 'lodash'
import { computed, onMounted, ref, unref } from 'vue-demi'
import allSettled from 'promise.allsettled'
import { getProjectAccounts, inviteProjectRep, upsertProjectAccounts } from '@/api/projects/request'
import HKLButton from '@/controls/Button/HKLButton.vue'
import HKLDeleteIcon from '@/controls/Icon/HKLDeleteIcon.vue'
import HKLInput from '@/controls/Input/HKLInput.vue'
import HKLInputEmail from '@/controls/Input/HKLInputEmail.vue'
import HKLModal from '@/controls/Modal/HKLModal.vue'
import HKLInviteModal from '@/controls/Modal/InviteModal'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { useStoreGetters } from '@/store/index'
import dateUtil from '@/utils/dateUtil'
import { displayI18nFullName, getFullnameSorts } from '@/utils/fullName'
import i18n from '@/utils/i18n'
import { projectStatus } from '@/utils/locale'
import { aclHelper } from '@/utils/aclHelper'
import Collapse from '@/controls/Collapse.vue'
import { validateEmails } from '@/controls/Input/HKLInputEmails.service'
import { getLink, getVendorHomeLink } from '@/api/postman/util'

export default {
  components: {
    Collapse,
    HKLDeleteIcon,
    HKLButton,
    HKLInput,
    HKLModal,
    HKLInputEmail,
    HKLInviteModal,
  },
  setup () {
    const { open: openInviteAllModal, close: closeInviteAllModal, modal: inviteAllRef } = useRefModal()
    const { open: openDelModal, modal: delModalRef } = useRefModal()
    const { currentProject } = useStoreGetters('projects', ['currentProject'])
    const { currentUser } = useStoreGetters('user', ['currentUser'])
    const invalidExternalEmails = ref([])
    const invalidInternalEmails = ref([])
    const projectId = computed(() => {
      return lodash.get(unref(currentProject), 'id')
    })
    let returnData = {
      projectId,
      editable: ref(false),
      rawAccounts: ref([]),
      pendingAccounts: ref([]),
      isPicView: aclHelper.isProjectPic(),
      headersMap: computed(() => {
        return [
          { labelKey: 'name', required: true },
          { labelKey: 'email', required: true },
          { labelKey: 'primaryPhone', required: true },
          { labelKey: 'otherPhone' },
          { labelKey: 'lastLogin' },
          { labelKey: 'invitedAt' },
        ]
      }),
      sortedFullName: computed(() => {
        return getFullnameSorts()
      }),
      deleteIndex: ref(null),
    }
    const refreshPendingData = async () => {
      const project = await getProjectAccounts(unref(projectId))
      returnData.rawAccounts.value = project.accounts
      const dateEmptyVal = '-'
      const dateFormatStr = 'L LT'
      const currentUserId = lodash.get(unref(currentUser), 'id')
      returnData.deleteIndex.value = null
      returnData.pendingAccounts.value = project.accounts.map(
        (item, index) => {
          const accountDetail = lodash.get(item, 'ProjectAccount')
          const pickedItems = lodash.pick(accountDetail, [
            'id',
            'firstName',
            'lastName',
            'phone',
            'altPhone',
            'invitedAt',
          ])
          const name = displayI18nFullName(pickedItems.firstName, pickedItems.lastName) || i18n.t('emptyValue')
          const invitedAt = dateUtil.formatByLongDate(pickedItems.invitedAt, dateFormatStr) || dateEmptyVal
          const lastLogin = dateUtil.formatByLongDate(item.lastLoginAt, dateFormatStr) || dateEmptyVal
          const email = item.username
          const showInviteClass = { hidden: item.ProjectAccount.userId === currentUserId }
          return {
            name,
            email,
            ...pickedItems,
            otherPhone: pickedItems.altPhone,
            primaryPhone: pickedItems.phone,
            invitedAt,
            lastLogin,
            userId: item.id,
            rawInvitedAt: pickedItems.invitedAt,
            showInviteClass,
          }
        })
    }
    const addAccount = () => {
      returnData.pendingAccounts.value.push({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        altPhone: '',
      })
    }
    const delAccount = (index) => {
      returnData.pendingAccounts.value.splice(index, 1)
    }
    const preDeleteAccount = (item, index) => {
      const hasInvited = lodash.get(item, 'rawInvitedAt')
      if (hasInvited) {
        returnData.deleteIndex.value = index
        openDelModal()
      } else {
        delAccount(index)
      }
    }
    const validate = (item) => {
      const elClassName = '.project-rep__form'
      fusionInputCheckUtil.validateAllFields({ allowEmpty: 'none' }, elClassName)
      return fusionInputCheckUtil.isAllFieldsValid(elClassName)
    }
    const checkInputEmail = (email) => {
      const isValidEmail = validateEmails()(email)
      const emailList = returnData.pendingAccounts.value.map(item => item.email)
      const isDuplicated = emailList.filter(item => item === email).length > 1
      return !isDuplicated && isValidEmail
    }
    const needInviteAccounts = computed(() => {
      return unref(returnData.rawAccounts).filter((item) => !item.ProjectAccount.invitedAt)
    })
    const canEditRepresentative = computed(() => {
      const allowedStatus = [projectStatus.DRAFT, projectStatus.ACTIVE]
      return unref(returnData.isPicView) && allowedStatus.includes(
        lodash.get(unref(currentProject), 'status'),
      )
    })
    const shouldOpenInviteAllModal = computed(() => {
      return unref(canEditRepresentative) && unref(needInviteAccounts).length > 0
    })
    const save = async () => {
      const isValid = validate()
      if (!isValid) {
        return
      }
      invalidExternalEmails.value = []
      invalidInternalEmails.value = []
      try {
        await upsertProjectAccounts(unref(projectId), unref(returnData.pendingAccounts))
      } catch (err) {
        invalidExternalEmails.value = lodash.get(err, 'response.data.data.invalidExtEmails', [])
        invalidInternalEmails.value = lodash.get(err, 'response.data.data.invalidIntEmails', [])
        throw err
      }
      returnData.editable.value = false
      await refreshPendingData()
      if (unref(shouldOpenInviteAllModal)) {
        openInviteAllModal()
      }
    }
    const invalidEmailClassesMap = computed(() => {
      return unref(returnData.pendingAccounts).map((item) => {
        const isInvalid = invalidExternalEmails.value.includes(item.email) || invalidInternalEmails.value.includes(item.email)
        return {'is-invalid-input': isInvalid}
      })
    })
    const requestLinks = {
      intAccessLink: getLink('home'),
      extAccessLink: getVendorHomeLink(),
    }
    const invite = async (id) => {
      await inviteProjectRep(unref(projectId), id, requestLinks)
      await refreshPendingData()
    }
    onMounted(async () => {
      await refreshPendingData()
    })
    return {
      ...returnData,
      checkInputEmail,
      invalidEmailClassesMap,
      canEditRepresentative,
      delModalRef,
      openDelModal,
      editHeadersMap: computed(() => {
        return unref(returnData.headersMap).slice(0, 4)
      }),
      addAccount,
      delAccount,
      preDeleteAccount,
      cancel: () => {
        returnData.editable.value = false
        refreshPendingData()
      },
      save,
      inviteAllRef,
      openInviteAllModal,
      needInviteAccounts,
      shouldOpenInviteAllModal,
      invite,
      inviteAll: async (users) => {
        const promises = lodash.castArray(users).map(async (user) => {
          const projectAccount = user.ProjectAccount
          await inviteProjectRep(unref(projectId), projectAccount.id, requestLinks)
        })
        await allSettled(promises)
        closeInviteAllModal()
        await refreshPendingData()
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.project-rep {
  max-width: 1440px;
  margin: auto;
  &.collapse ::v-deep {
    .default-title {
      font-family: Georgia;
      font-size: 22px;
      font-weight: normal;
      line-height: 25px;
      text-align: left;
      color: #0c3a58;
    }
    .title-container {
      border-bottom: 1px solid #e0e0e0;
    }
    .title-content {
      margin-right: 0;
      padding-left: 24px;
    }
  }
  &__box {
    padding: 20px 24px;
  }
  &__header {
    display: flex;
    justify-content: space-between;
    padding-bottom: 15px;
  }
  &__title {
    font-size: 16px;
    font-weight: bold;
  }
  &__add {
    font-style: italic;
  }
}
.flex-table {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 15px;
  &__header {
    font-size: 12px;
    height: 60px;
    color: gray;
    display: flex;
    align-items: center;
    width: 100%;
    height: 40px;
    &-label {
      height: auto;
    }
  }
  &__body {
    display: flex;
    align-items: center;
    width: 100%;
    margin-bottom: 5px;
    height: 40px;
    .name {
      font-weight: bold;
    }
  }
  &__col {
    padding-right: 20px;
    flex: 1.4;
    word-break: break-word;
    word-wrap: break-word;
    &.email, &.input-email {
      flex: 1.8
    }
    &.input-name {
      display: flex;
    }
  }
  .invite-col {
    line-height: 60px;
    width: 80px;
  }
  &__del-col {
    width: 14px
  }
  .hidden {
    visibility: hidden;
  }
}
</style>
